export const MODE = (import.meta.env.MODE?.trim() || 'development') as
  | 'development'
  | 'production';

export const VERSION = (import.meta.env.VITE_VERSION as string)?.trim();

const trimSlash = (s: string) => s?.replace(/^\/+|\/+$/g, '');

export const BASE = trimSlash(import.meta.env.BASE_URL?.trim()!);

export type WorkspaceType = 'public' | 'app' | 'admin';

const detectWorkspace = (): WorkspaceType => {
  const segments = window.location.pathname.split('/').filter((x) => !!x);
  const first = BASE && segments[0] === BASE ? segments[1] : segments[0];
  return first === 'app' || first === 'admin' ? first : 'public';
};

export const WORKSPACE = trimSlash(
  (import.meta.env.VITE_WORKSPACE as string)?.trim() || detectWorkspace(),
) as WorkspaceType;

export const IS_PRERENDERING = (() =>
  typeof window !== 'undefined' &&
  (window as { __IQX_PRERENDERING__?: boolean }).__IQX_PRERENDERING__ ===
    true)();

export const BASE_URL = [
  IS_PRERENDERING ? 'https://iqx.io' : window.location.origin,
  BASE,
]
  .filter((x) => !!x)
  .join('/');

export const SEO_ORIGIN = 'https://iqx.io';

export const IS_PRODUCTION = MODE !== 'development' && BASE.length <= 1;

export const DEV_OVERRIDES_STORAGE_KEY = 'iqx-dev-constant-overrides';

// A stored `null` means "unset": the constant resolves to undefined
// without falling back to the build value.
const devOverrides: Record<string, string | null> = (() => {
  try {
    const parsed = JSON.parse(
      localStorage.getItem(DEV_OVERRIDES_STORAGE_KEY) ?? '{}',
    );
    return Object.fromEntries(
      Object.entries(parsed).filter(
        ([, v]) => typeof v === 'string' || v === null,
      ),
    ) as Record<string, string | null>;
  } catch {
    return {};
  }
})();

const withDevOverride = (key: string, buildValue?: string) => {
  const override = devOverrides[key];
  if (override === null) return undefined;
  return override?.trim() || buildValue?.trim() || undefined;
};

if (!import.meta.env.VITE_ACCOUNT_API_BASE_URL)
  throw new Error('VITE_ACCOUNT_API_BASE_URL env is missing');

export const ACCOUNT_API_BASE_URL = trimSlash(
  withDevOverride(
    'VITE_ACCOUNT_API_BASE_URL',
    import.meta.env.VITE_ACCOUNT_API_BASE_URL as string,
  )!,
);

if (!import.meta.env.VITE_GOOGLE_CLIENT_ID)
  throw new Error('VITE_GOOGLE_CLIENT_ID env is missing');
export const GOOGLE_CLIENT_ID = withDevOverride(
  'VITE_GOOGLE_CLIENT_ID',
  import.meta.env.VITE_GOOGLE_CLIENT_ID as string,
)!;

export const SENTRY_DSN =
  withDevOverride('VITE_SENTRY_DSN', import.meta.env.VITE_SENTRY_DSN) ?? null;

export const USERBACK_ID =
  withDevOverride('VITE_USERBACK_ID', import.meta.env.VITE_USERBACK_ID) ?? null;

export const RECAPTCHA_KEY =
  withDevOverride('VITE_RECAPTCHA_KEY', import.meta.env.VITE_RECAPTCHA_KEY) ??
  null;

export const RECAPTCHA_V2_KEY =
  withDevOverride(
    'VITE_RECAPTCHA_V2_KEY',
    import.meta.env.VITE_RECAPTCHA_V2_KEY,
  ) ?? null;

export const ONESIGNAL_APP_ID =
  withDevOverride(
    'VITE_ONESIGNAL_APP_ID',
    import.meta.env.VITE_ONESIGNAL_APP_ID,
  ) ?? null;

export type DevOverridableConstant = {
  key: string;
  label: string;
  buildValue: string;
  overrideValue: string | null;
  unset: boolean;
};

export const DEV_OVERRIDABLE_CONSTANTS: DevOverridableConstant[] = [
  {
    key: 'VITE_ACCOUNT_API_BASE_URL',
    label: 'Account API Base URL',
    buildValue: import.meta.env.VITE_ACCOUNT_API_BASE_URL as string,
  },
  {
    key: 'VITE_GOOGLE_CLIENT_ID',
    label: 'Google Client ID',
    buildValue: import.meta.env.VITE_GOOGLE_CLIENT_ID as string,
  },
  {
    key: 'VITE_SENTRY_DSN',
    label: 'Sentry DSN',
    buildValue: import.meta.env.VITE_SENTRY_DSN as string,
  },
  {
    key: 'VITE_USERBACK_ID',
    label: 'Userback ID',
    buildValue: import.meta.env.VITE_USERBACK_ID as string,
  },
  {
    key: 'VITE_RECAPTCHA_KEY',
    label: 'reCAPTCHA Key',
    buildValue: import.meta.env.VITE_RECAPTCHA_KEY as string,
  },
  {
    key: 'VITE_RECAPTCHA_V2_KEY',
    label: 'reCAPTCHA V2 Key',
    buildValue: import.meta.env.VITE_RECAPTCHA_V2_KEY as string,
  },
  {
    key: 'VITE_ONESIGNAL_APP_ID',
    label: 'OneSignal App ID',
    buildValue: import.meta.env.VITE_ONESIGNAL_APP_ID as string,
  },
].map((c) => ({
  ...c,
  buildValue: c.buildValue?.trim() ?? '',
  overrideValue: devOverrides[c.key]?.trim() || null,
  unset: devOverrides[c.key] === null,
}));

export const ROOT_EL = document.querySelector('#root')!;
